import {
  HttpEventType,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { defer, finalize, tap } from 'rxjs';
import { SKIP_PENDING_MUTATION_GUARD } from '@application/data-access';

// Defined in `@application/data-access` (see there why); re-exported so the
// existing `@application/http-client` imports keep working.
export { SKIP_PENDING_MUTATION_GUARD };

/**
 * Methods considered "mutations": requests that change server state and whose
 * interruption can leave the backend half-way through an operation. Read-only
 * verbs (GET/HEAD/OPTIONS) are safe to abort, so they never block the unload.
 */
const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Upper bound on how long a single request may hold the guard. A request that
 * outlives this is either hung or parked on an upstream observable that never
 * terminates (e.g. `TokenInterceptor` waiting on a token refresh that failed —
 * its `refreshTokenSubject` swallows the `null` it emits on failure, so the
 * chain never completes and `finalize` never fires). Blocking the page forever
 * is far worse than losing the guard on a request nobody is waiting for.
 */
const MAX_GUARD_HOLD_MS = 30_000;

/**
 * Mutating requests currently in flight, keyed by an incrementing id so the same
 * URL can be in flight twice. Holding the descriptions (rather than a bare
 * counter) is what makes a stuck guard diagnosable: the unload handler can name
 * the requests that are blocking the page.
 */
const inFlightMutations = new Map<number, string>();
let nextMutationId = 0;
let unloadGuardRegistered = false;

/** `true` while at least one POST/PUT/PATCH/DELETE is in flight. */
export function hasPendingMutations(): boolean {
  return inFlightMutations.size > 0;
}

/**
 * Descriptions (`"POST https://…"`) of the mutations currently holding the guard.
 * Exposed for debugging a page that refuses to unload.
 */
export function getPendingMutations(): string[] {
  return [...inFlightMutations.values()];
}

/**
 * Registers the native "leave site?" confirmation, once, on the first mutating
 * request. Calling `preventDefault()` (plus the legacy `returnValue`, still
 * required by Safari and older Chrome) makes the browser show its own dialog —
 * the text is fixed by the browser and cannot be customized.
 *
 * The dialog only appears if the user has interacted with the page (sticky
 * activation), which is always the case for a request triggered by a click.
 */
function registerUnloadGuard(): void {
  if (unloadGuardRegistered || typeof window === 'undefined') return;
  unloadGuardRegistered = true;

  window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => {
    if (!hasPendingMutations()) return;

    console.warn(
      '[PendingMutationGuard] blocking unload, in flight:',
      getPendingMutations()
    );

    event.preventDefault();
    event.returnValue = '';
  });
}

/**
 * HTTP interceptor that blocks a page reload / tab close / navigation away while
 * a write request (POST, PUT, PATCH, DELETE) is still in flight, asking the user
 * to confirm through the browser's native dialog.
 *
 * Counting is scoped to the subscription: `defer` moves the increment out of the
 * interceptor body (which runs while the chain is merely being *built*, and an
 * upstream interceptor may build a chain it never subscribes to) into the
 * subscribe itself, pairing it with the `finalize` that releases it. The
 * decrement therefore runs on success, on error and on unsubscribe
 * (`switchMap`, `takeUntilDestroyed`, a destroyed component) — a cancelled
 * request never leaves the guard armed — and {@link MAX_GUARD_HOLD_MS} covers
 * the remaining case of a chain that never terminates at all.
 *
 * Only covers document-level navigation. In-app Angular route changes do not
 * fire `beforeunload`; use a `CanDeactivate` guard for those.
 *
 * Opt out per request with {@link SKIP_PENDING_MUTATION_GUARD}.
 */
export const PendingMutationInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): ReturnType<HttpHandlerFn> => {
  const isGuarded =
    MUTATION_METHODS.has(request.method.toUpperCase()) &&
    !request.context.get(SKIP_PENDING_MUTATION_GUARD);

  if (!isGuarded) {
    return next(request);
  }

  return defer(() => {
    registerUnloadGuard();

    const id = nextMutationId++;
    inFlightMutations.set(id, `${request.method} ${request.urlWithParams}`);

    const release = () => inFlightMutations.delete(id);
    const holdTimeout = setTimeout(release, MAX_GUARD_HOLD_MS);

    return next(request).pipe(
      // Release on the response *event*, not on completion. The guard exists to
      // stop the user leaving while the server is half-way through a write —
      // once the response is in, that risk is over, and holding on until
      // `complete` is actively harmful: `complete` only fires after every `next`
      // handler up the chain has returned, and those handlers are where the app
      // redirects (e.g. `transportBearerTo` assigning `window.location.href`
      // after login). Waiting for it means the guard is still armed at the exact
      // moment the app navigates — a request that DevTools already shows as 200
      // blocks its own redirect.
      //
      // This interceptor is first in the chain, so it is the outermost wrapper:
      // this `tap` runs before the response reaches any caller-side handler.
      tap((event) => {
        if (event.type === HttpEventType.Response) {
          clearTimeout(holdTimeout);
          release();
        }
      }),
      finalize(() => {
        clearTimeout(holdTimeout);
        release();
      })
    );
  });
};
